import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { masterAssemblyApi } from "../../apiServices/masterassembly";
import { useAuthoringStore } from "../../store/UseAuthorStore";
import { useUserStore } from "../../store/userStore";
import "./CreateNewPartnumber.css";

const normalizeMediaPath = (path: string) => {
  const p = String(path || "").trim();
  if (!p) return "";
  const mediaIdx = p.indexOf("/media/");
  if (mediaIdx >= 0) {
    return p.slice(mediaIdx + "/media/".length);
  }
  if (p.startsWith("media/")) return p.slice("media/".length);
  if (p.startsWith("/media/")) return p.slice("/media/".length);
  return p;
};

const CreateNewPartnumber: React.FC = () => {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";

  const [partNumber, setPartNumber] = useState("");
  const [componentName, setComponentName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const descriptionValue = useMemo(() => {
    if (componentName && description) return `${componentName} — ${description}`;
    return description || componentName;
  }, [componentName, description]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    if (!selected) {
      setFile(null);
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (selected.size > maxSize) {
      setFile(null);
      setError("File size must be 5MB or less.");
      e.target.value = "";
      return;
    }

    setError(null);
    setFile(selected);
  };

  const handleCreate = async () => {
    if (!partNumber.trim()) {
      setError("Part number is required.");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const store = useAuthoringStore.getState();
      store.setOEMName(oem);
      store.setPartNumber(partNumber.trim());
      store.setPartDescription(descriptionValue.trim().toUpperCase());
      store.setPartRevision("1");

      const payload = store.buildAddPayload();
      const resp = await masterAssemblyApi.addMaster(oem, payload);

      if (resp?.statusCode !== 200 || !resp?.data) {
        throw new Error(resp?.message || "Failed to create component");
      }

      store.setMasterAssembly(resp.data);

      if (file) {
        const masterAssemblyId = resp.data?.masterAssemblyId ?? 0;
        if (masterAssemblyId) {
          const svg = await masterAssemblyApi.uploadSvg({
            file,
            masterAssemblyId,
            figNumber: "main",
            oem,
          });
          if (!svg || !svg.svgId) {
            throw new Error("SVG upload failed");
          }
          const normalizedPath = normalizeMediaPath(svg.folderPath);
          store.setMainSvg({
            svgId: svg.svgId,
            folderPath: normalizedPath,
            fileName: svg.fileName,
          });
          const updatePayload = store.buildAddPayload();
          updatePayload.masterAssemblyId = masterAssemblyId;
          await masterAssemblyApi.updateMaster(oem, updatePayload);
        }
      }

      navigate("/authoring");
    } catch (e: any) {
      setError(e?.message || "Failed to create component");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-partnumber">
      <div className="create-partnumber__card">
        <div className="create-partnumber__header">
          <h2>Create New Component</h2>
        </div>

        <div className="create-partnumber__body">
          <div className="create-partnumber__image">
            <p className="create-partnumber__label">Component Image</p>
            <label className="create-partnumber__dropzone">
              <input
                type="file"
                accept=".svg,.png,.jpg,.jpeg"
                onChange={handleFileChange}
              />
              {previewUrl ? (
                <>
                  <div className="create-partnumber__previewbox">
                    <img className="create-partnumber__previewimg" src={previewUrl} alt={file?.name || "Preview"} />
                  </div>
                  <div className="create-partnumber__droptext">
                    <span>Click to replace image</span>
                    <small>PNG, JPG, SVG up to 5MB</small>
                  </div>
                </>
              ) : (
                <>
                  <div className="create-partnumber__dropicon">+</div>
                  <div className="create-partnumber__droptext">
                    <span>Click to upload image</span>
                    <small>PNG, JPG, SVG up to 5MB</small>
                  </div>
                </>
              )}
            </label>
            {file && <div className="create-partnumber__filename">{file.name}</div>}
          </div>

          <div className="create-partnumber__fields">
            <div className="create-partnumber__field">
              <p className="create-partnumber__label">Part Number</p>
              <input
                className="create-partnumber__input"
                value={partNumber}
                onChange={(e) => setPartNumber(e.target.value)}
                placeholder="e.g. PRLHT-001"
              />
            </div>

            <div className="create-partnumber__field">
              <p className="create-partnumber__label">Component Name</p>
              <input
                className="create-partnumber__input"
                value={componentName}
                onChange={(e) => setComponentName(e.target.value.toUpperCase())}
                placeholder="e.g. Landing Gear Assembly"
              />
            </div>

            {/* <div className="create-partnumber__field">
              <p className="create-partnumber__label">Description</p>
              <textarea
                className="create-partnumber__textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value.toUpperCase())}
                placeholder="Enter detailed component description, specs, or compatibility notes..."
              />
            </div> */}
          </div>
        </div>

        {error && <div className="create-partnumber__error">{error}</div>}

        <div className="create-partnumber__footer">
          <button type="button" className="create-partnumber__btn outline" onClick={() => navigate("/authoring")}>
            Cancel
          </button>
          <button type="button" className="create-partnumber__btn" onClick={handleCreate} disabled={loading}>
            {loading ? "Creating..." : "Create Component"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateNewPartnumber;
